import { useState, useCallback } from 'react';

export const useApiRequest = (options = {}) => {
  const {
    showErrorInConsole = true,
    onError = null,
    onSuccess = null,
    handle401 = true
  } = options;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const execute = useCallback(async (apiFunction, requestOptions = {}) => {
    const {
      showLoading = true,
      clearErrorOnStart = true,
      onRequestError = null,
      onRequestSuccess = null,
      errorMessage = null
    } = requestOptions;

    try {
      if (showLoading) {
        setLoading(true);
      }
      if (clearErrorOnStart) {
        setError(null);
      }

      const result = await apiFunction();

      setData(result);

      if (onRequestSuccess) {
        onRequestSuccess(result);
      }
      if (onSuccess) {
        onSuccess(result);
      }

      return result;
    } catch (err) {
      const finalError = errorMessage || err.message || 'An error occurred';

      setError(finalError);

      if (showErrorInConsole) {
        console.error('API Request Error:', err);
      }

      if (handle401 && (err.status === 401 || finalError.includes('401'))) {
        console.log('Authentication error (401) detected, logging out user');
        localStorage.removeItem('authToken');
        window.location.href = '/login';
        return null;
      }

      if (onRequestError) {
        onRequestError(err);
      }
      if (onError) {
        onError(err);
      }

      return null;
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [showErrorInConsole, onError, onSuccess, handle401]);

  /**
   * Reset the hook state
   */
  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setData(null);
  }, []);

  /**
   * Clear only the error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    data,
    execute,
    reset,
    clearError,
    setLoading,
    setError,
    setData
  };
};

/**
 * Simplified hook for single API requests
 * Automatically executes on mount and provides manual refetch
 *
 * @param {Function} apiFunction - Async function that makes the API call
 * @param {Object} options - Configuration options
 * @returns {Object} Request state and utilities
 */
export const useApiQuery = (apiFunction, options = {}) => {
  const {
    enabled = true,
    onSuccess = null,
    onError = null,
    dependencies = []
  } = options;

  const apiRequest = useApiRequest({ onSuccess, onError });

  const refetch = useCallback(() => {
    return apiRequest.execute(apiFunction);
  }, [apiFunction, apiRequest]);

  // Auto-execute on mount or when dependencies change
  useState(() => {
    if (enabled) {
      refetch();
    }
  });

  return {
    ...apiRequest,
    refetch
  };
};

/**
 * Hook for handling multiple API requests in parallel
 * Useful for fetching multiple resources at once
 *
 * @param {Object} options - Configuration options
 * @returns {Object} API utilities for parallel requests
 */
export const useParallelApiRequests = (options = {}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [data, setData] = useState({});

  /**
   * Execute multiple API requests in parallel
   * @param {Object} requests - Object with keys as identifiers and values as async functions
   * @returns {Promise<Object>} Results keyed by request identifiers
   */
  const executeAll = useCallback(async (requests) => {
    setLoading(true);
    setErrors({});

    const results = {};
    const errorMap = {};

    await Promise.allSettled(
      Object.entries(requests).map(async ([key, apiFunction]) => {
        try {
          const result = await apiFunction();
          results[key] = result;
        } catch (err) {
          errorMap[key] = err.message || 'An error occurred';
          console.error(`API Request Error [${key}]:`, err);
        }
      })
    );

    setData(results);
    setErrors(errorMap);
    setLoading(false);

    return results;
  }, []);

  const reset = useCallback(() => {
    setLoading(false);
    setErrors({});
    setData({});
  }, []);

  return {
    loading,
    errors,
    data,
    executeAll,
    reset,
    hasErrors: Object.keys(errors).length > 0
  };
};
